// Minimal, framework-agnostic CardConnect HIFT adapter
// Usage:
//   const detach = attach(containerEl, { env: 'uat'|'prod', onToken, onError })
//   detach() to cleanup

function buildIframeSrc(env) {
  console.log('buildIframeSrc', env);
  const subdomain = env === 'uat' ? 'boltgw-uat' : 'boltgw';
  // Using provided FlexPay params; CVV excluded by design
  const css =
    'input%2Cselect%7Bborder-radius%3A5px%3B%20font-size%3A1.1em%3B%20margin%3A5px%3B%20margin-left%3A5px%3B%20height%3A%2045px%3B%20border%3A%201px%20solid%20%23CCC%3B%20padding-left%3A7px%3B%20box-sizing%3A%20border-box%3B-webkit-appearance%3A%20none%3B%7Dselect%7Bpadding-right%3A16px%3B%7D.error%7Bborder%3A%20solid%201px%20red%3B%7D%23cccvvfield%7Bwidth%3A60px%3B%7D%23cccvvlabel%7Bmargin-left%3A%2040px%3B%20margin-top%3A%202px%3B%7D%2523ccnumfield%7Bwidth%3A100%25%3B%7Dinput%3Afocus%2Cselect%3Afocus%7Boutline%3Anone%3B%20border%3A1px%20solid%20%2314CEFB%3B%7Dbody%7Bmargin-left%3A5px%3B%20font-family%3A%20Arial%2CSans-serif%3B%20white-space%3Anowrap%3B%7Dlabel%7Bmargin-left%3A%205px%3B%20font-size%3A.9em%3B%7D';
  const params =
    'orientation=horizontal&css=' +
    css +
    '&useexpiry=true&formatinput=true&tokenizewheninactive=true&inactivityto=500&invalidinputevent=true';
  return 'https://' + subdomain + '.cardconnect.com/itoke/ajax-tokenizer.html?' + params;
}

function parseMessageData(data) {
  console.log('parseMessageData', data);
  try {
    const msg = typeof data === 'string' ? JSON.parse(data) : data;
    if (!msg || typeof msg !== 'object') return null;
    // Treat validation/invalid flags as error
    if (msg.invalid === true || msg.error || msg.validationError) return { error: true };

    // CardConnect HIFT may send the token under `message` (FlexPay) or `token` (API-style)
    let token = null;
    if (typeof msg.message === 'string' && msg.message && msg.message !== 'No error') {
      token = msg.message;
    } else if (typeof msg.token === 'string' && msg.token) {
      token = msg.token;
    }

    // Expiry can vary; coerce to a digit string if present, but do not enforce shape
    let expiry = msg.expiry;
    if (expiry !== undefined && expiry !== null) {
      if (typeof expiry !== 'string') expiry = String(expiry);
      expiry = expiry.replace(/\D/g, '');
      if (!expiry) expiry = undefined;
    }

    if (token) return { token, expiry };
    return null;
  } catch (_e) {
    return null;
  }
}

export function attach(containerEl, { env = 'prod', onToken, onError } = {}) {
  console.log('attach', containerEl, { env, onToken, onError });
  if (!containerEl) throw new Error('CardConnect adapter: containerEl is required');

  // Clean container
  while (containerEl.firstChild) containerEl.removeChild(containerEl.firstChild);

  // Telemetry (non-sensitive)
  const sessionId = Math.random().toString(36).slice(2);
  const attachTs = Date.now();
  let msgCount = 0;
  let originMismatches = 0;
  let parseNulls = 0;
  let validationErrors = 0;
  let tokensAccepted = 0;
  let firstTokenTs = null;

  const iframe = document.createElement('iframe');
  iframe.id = 'tokenFrame';
  iframe.name = 'tokenFrame';
  iframe.className = 'iframe';
  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute('scrolling', 'no');
  iframe.src = buildIframeSrc(env);
  containerEl.appendChild(iframe);

  const allowedOrigin =
    env === 'uat'
      ? 'https://boltgw-uat.cardconnect.com'
      : 'https://boltgw.cardconnect.com';

  function previewData(data) {
    try {
      const s = typeof data === 'string' ? data : JSON.stringify(data);
      // redact long digit runs and obvious tokens
      const redacted = s
        .replace(/[0-9]{6,}/g, '[[redacted-digits]]')
        .replace(/"token"\s*:\s*"[^"]+"/gi, '"token":"[[redacted-token]]"')
        .slice(0, 128);
      return redacted;
    } catch (_e) {
      return '[unserializable]';
    }
  }

  console.log('[CC-HIFT] attach', {
    sessionId: sessionId,
    env: env,
    allowedOrigin: allowedOrigin,
    iframeSrc: iframe.src,
    ts: new Date(attachTs).toISOString(),
  });

  let isActive = true;

  iframe.addEventListener('load', function () {
    console.log('[CC-HIFT] iframe-load', { sessionId: sessionId });
  });
  iframe.addEventListener('error', function () {
    console.log('[CC-HIFT] iframe-error', { sessionId: sessionId });
  });

  function handleMessage(event) {
    console.log('handleMessage', {
      event,
      isActive,
      allowedOrigin,
    });
    if (!isActive) return;
    msgCount += 1;
    const originOk = !!event && event.origin === allowedOrigin;

    console.log('[CC-HIFT] message', {
      sessionId: sessionId,
      n: msgCount,
      originOk: originOk,
      origin: event && event.origin,
      sameSource: !!(
        iframe.contentWindow &&
        event &&
        event.source === iframe.contentWindow
      ),
      preview: previewData(event && event.data),
    });

    if (!originOk) {
      originMismatches += 1;
      return;
    }

    const parsed = parseMessageData(event.data);
    if (!parsed) {
      parseNulls += 1;
      if (msgCount < 10 || msgCount % 10 === 0) {
        console.log('[CC-HIFT] parse-null', { sessionId: sessionId, n: msgCount });
      }
      return;
    }
    if (parsed.error) {
      validationErrors += 1;
      console.log('[CC-HIFT] validation/error', { sessionId: sessionId, n: msgCount });
      try {
        if (onError) onError(true);
      } catch (_e) {}
      return;
    }

    tokensAccepted += 1;
    if (!firstTokenTs) firstTokenTs = Date.now();
    console.log('[CC-HIFT] token-accepted', {
      sessionId: sessionId,
      ageMs: firstTokenTs - attachTs,
      expiryLen: parsed.expiry ? String(parsed.expiry).length : 0,
    });
    try {
      if (onToken) onToken(parsed);
    } catch (_e) {}
  }

  window.addEventListener('message', handleMessage, false);

  function detach() {
    console.log('detach');
    isActive = false;
    window.removeEventListener('message', handleMessage, false);
    try {
      if (iframe && iframe.parentNode === containerEl) {
        containerEl.removeChild(iframe);
      }
    } catch (_e) {}
    console.log('[CC-HIFT] detach', {
      sessionId: sessionId,
      lifetimeMs: Date.now() - attachTs,
      msgCount: msgCount,
      originMismatches: originMismatches,
      parseNulls: parseNulls,
      validationErrors: validationErrors,
      tokensAccepted: tokensAccepted,
      gotFirstTokenMs: firstTokenTs ? firstTokenTs - attachTs : null,
    });
  }

  return detach;
}

export default { attach };
